import {
  hasResolvedDocumentValue,
  useDocument,
  useDocumentsTogether,
} from "@shared/components/providers/SupergrainProvider"
import type { CardStack, CardStackSummary, UnifiedCardStack } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { CourseDateCombined as CourseDateCombinedModel } from "@shared/models/course-date-combined"
import type { Permission } from "@shared/models/permissions"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { tracked } from "@supergrain/kernel/react"
import { type ReactNode } from "react"
import CourseDateCombined from "react-app/components/planner/course/date/CourseDateCombined"
import { applyTemplateToLessonIfNeeded, usePhantomLessonDeps } from "react-app/lib/phantom-lesson"

type Lesson = UnifiedCardStack & { attributes: { usedAs: "lesson" } }

/**
 * The Ember block params in order, then the documents the provider read — nothing below reads them
 * again. The block runs whether or not the lesson has landed; each consumer writes its own else.
 */
type Yield<Doc> = (
  courseDate: CourseDateCombinedModel,
  cardStack: Doc | null,
  units: Array<CardStackSummary> | null,
  cardStackIsFulfilled: boolean,
  course: Course,
  planbook: Planbook,
  rotationCalendar: RotationCalendar,
) => ReactNode

interface BaseProps {
  courseId: string
  planbookId: string
  rotationCalendarId: string
  dateString: string
}

/** The Ember provider's arguments; the two flags discriminate the yielded document type. */
type Props =
  | (BaseProps & {
      useStudentVersion?: false
      useCardStackSummary?: false
      children: Yield<Lesson>
    })
  | (BaseProps & {
      useStudentVersion?: false
      useCardStackSummary: true
      children: Yield<CardStackSummary | CardStack>
    })
  | (BaseProps & {
      useStudentVersion: true
      useCardStackSummary?: false
      children: Yield<CardStack>
    })

/** The widened callback the internals use; the public `Props` narrows it per call site. */
type AnyYield = (
  courseDate: CourseDateCombinedModel,
  cardStack: never,
  units: Array<CardStackSummary> | null,
  cardStackIsFulfilled: boolean,
  course: Course,
  planbook: Planbook,
  rotationCalendar: RotationCalendar,
) => ReactNode

/** `Planner::CardStacks::Lesson::Provider` for React: `lesson/provider.hbs`, composition and all. */
export default tracked(function CardStackProvider(props: Props) {
  return (
    <CourseDateCombined
      courseId={props.courseId}
      planbookId={props.planbookId}
      rotationCalendarId={props.rotationCalendarId}
      dateString={props.dateString}
    >
      {(courseDate, course, planbook, rotationCalendar) => (
        <Provider
          planbookId={planbook.id}
          ownerId={course.id}
          cardStackId={courseDate.cardStackId}
          phantomCardStackId={courseDate.phantomCardStackId}
          cardStackTemplateId={courseDate.cardStackTemplateId}
          usedAs="lesson"
          useStudentVersion={props.useStudentVersion}
          useCardStackSummary={props.useCardStackSummary}
          unitIds={courseDate.unitIds}
          dateString={props.dateString}
          defaultPermissions={course.meta.permissions ?? []}
        >
          {(cardStackIsFulfilled, cardStack, units) =>
            (props.children as AnyYield)(
              courseDate,
              cardStack,
              units,
              cardStackIsFulfilled,
              course,
              planbook,
              rotationCalendar,
            )
          }
        </Provider>
      )}
    </CourseDateCombined>
  )
})

/**
 * `Planner::CardStacks::Provider` for React: the day's card stack, or the phantom standing in for
 * it. The store recomputes what Ember's proxy caches memoized, so the two `_return*` forks are two
 * expressions here; phantom identity lives in `phantom-lesson.ts`'s store scan.
 */
const Provider = tracked(function Provider({
  planbookId,
  ownerId,
  cardStackId,
  phantomCardStackId,
  cardStackTemplateId,
  usedAs,
  useStudentVersion,
  useCardStackSummary,
  unitIds,
  dateString,
  defaultPermissions,
  children,
}: {
  planbookId: string
  ownerId: string
  cardStackId: string | null
  phantomCardStackId: string | null
  cardStackTemplateId: string | null
  usedAs: "lesson"
  useStudentVersion?: boolean
  useCardStackSummary?: boolean
  unitIds: Array<string>
  dateString: string
  defaultPermissions: Array<Permission>
  children: (
    cardStackIsFulfilled: boolean,
    cardStack: never,
    units: Array<CardStackSummary> | null,
  ) => ReactNode
}) {
  const deps = usePhantomLessonDeps()

  // `@findMany("unitIds", "card-stack-summary")`.
  const units = useDocumentsTogether("card-stack-summary", unitIds).value ?? null

  const templateHandle = useDocument("card-stack", cardStackTemplateId)
  const template = hasResolvedDocumentValue(templateHandle) ? templateHandle.value : null

  // `_returnPhantomCardStack`: the phantom `CourseDateCombined`'s finder minted, unfilled until
  // the apply below lands its template — or blank content, when the day has none.
  const phantomId = usedAs === "lesson" && cardStackId === null ? phantomCardStackId : null
  const id = cardStackId ?? phantomId

  let model: "card-stack" | "card-stack-summary" | "class-website-lesson" = useCardStackSummary
    ? "card-stack-summary"
    : "card-stack"
  model = useStudentVersion ? "class-website-lesson" : model

  // Only the card-stack model tops up from the template — the same guard Ember puts around its
  // apply calls. A template that failed to load applies as null; the blank fallback.
  if (model === "card-stack" && id !== null && templateHandle.status !== "pending") {
    applyTemplateToLessonIfNeeded(deps, id, cardStackTemplateId, template)
  }

  // A phantom is always a card-stack — what phantom-lesson.ts mints — so a summary or
  // student-version call site reads its stand-in back as one.
  const handle = useDocument(cardStackId !== null ? model : "card-stack", id)
  const resolved = hasResolvedDocumentValue(handle) ? handle.value : null

  // Handing over a lesson still filling from its template makes the editor build twice. Only the
  // card-stack model fills; the others read the unfilled phantom as Ember reads its summary mint.
  const stillFillingFromTemplate =
    model === "card-stack" &&
    resolved !== null &&
    cardStackTemplateId !== null &&
    "proseMirror" in resolved.attributes &&
    resolved.attributes.proseMirror === null

  // `(and cardStack.isFulfilled (not (not cardStack.content)))` on the Ember side.
  const cardStackIsFulfilled = resolved !== null && !stillFillingFromTemplate

  return children(cardStackIsFulfilled, (cardStackIsFulfilled ? resolved : null) as never, units)
})
